import React, { useEffect, useState } from "react";
import { Button, Checkbox, message, Popconfirm } from "antd";
import { T } from "pages/Issue/theme";

const navyBtn = { background: T.navy, borderColor: T.navy };

// ─── Bulk selection ───────────────────────────────────────────────────────
//
// Every tab here is a list of decisions, and reviewers do not get them one
// at a time: forty checklist completions from one crew, a dozen imported
// line items, six progress reports filed the same afternoon. So each tab
// gets the same three pieces — a checkbox on every row, a "Check all" that
// follows the CURRENT filter, and the tab's own actions at the top acting
// on whatever is checked.
//
// Two rules the hook enforces so a bulk action can never surprise anyone:
//   1. Selection is scoped to what is visible. Change the project, the
//      search or the date and rows that disappear are dropped from the
//      selection - acting on a row the reviewer cannot see is exactly the
//      accident this is meant to prevent.
//   2. "Check all" means "all the rows I am looking at", never the whole
//      unfiltered queue.
export function useBulkSelection(visibleIds) {
  const [selected, setSelected] = useState(() => new Set());
  const visibleKey = (visibleIds || []).join("|");

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(visibleIds || []);
      const next = new Set();
      for (const id of prev) if (visible.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
    // visibleKey is the value that actually changes; visibleIds is a new
    // array identity on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey]);

  const ids = visibleIds || [];
  const selectedIds = ids.filter((id) => selected.has(id));
  const allChecked = ids.length > 0 && selectedIds.length === ids.length;
  const someChecked = selectedIds.length > 0 && !allChecked;

  return {
    selectedIds,
    count: selectedIds.length,
    isSelected: (id) => selected.has(id),
    toggle: (id) => setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    }),
    // Replace the whole selection (antd's Table hands us the full set).
    replace: (list) => setSelected(new Set(list || [])),
    setMany: (list, on) => setSelected((prev) => {
      const next = new Set(prev);
      for (const id of list) { if (on) next.add(id); else next.delete(id); }
      return next;
    }),
    toggleAll: () => setSelected((prev) => (allChecked ? new Set() : new Set(ids))),
    clear: () => setSelected(new Set()),
    allChecked,
    someChecked,
    total: ids.length,
  };
}

// The bar that sits above a tab's list. `actions` is
// [{ key, label, icon?, danger?, primary?, confirm?, run(ids) }] - one
// entry per thing this tab can do to a batch. `confirm` is the text of a
// Popconfirm the action has to pass first: for the ones that override a
// rule rather than apply it, where the whole point is that the click was
// deliberate.
export function BulkBar({ sel, actions, busy, hint }) {
  const [running, setRunning] = useState("");
  if (!sel.total) return null;
  const run = async (a) => {
    if (!sel.count) return;
    setRunning(a.key);
    try {
      await a.run(sel.selectedIds);
      sel.clear();
    } finally {
      setRunning("");
    }
  };
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      padding: "8px 12px", marginBottom: 10, borderRadius: 6,
      border: `1px solid ${sel.count ? "#F1DFB1" : T.line}`,
      background: sel.count ? "#FFFBF3" : "#FAFBFC",
    }}>
      <Checkbox
        checked={sel.allChecked}
        indeterminate={sel.someChecked}
        onChange={sel.toggleAll}
      >
        {sel.allChecked ? "Clear all" : `Check all (${sel.total})`}
      </Checkbox>
      <span style={{ fontSize: 12, color: T.muted }}>
        {sel.count ? `${sel.count} selected` : "nothing selected"}
        {hint ? ` · ${hint}` : ""}
      </span>
      <span style={{ flex: 1 }} />
      {actions.map((a) => {
        const btn = (
          <Button
            key={a.key}
            size="small"
            type={a.primary ? "primary" : "default"}
            style={a.primary ? navyBtn : undefined}
            danger={!!a.danger}
            icon={a.icon}
            disabled={!sel.count || !!busy}
            loading={running === a.key}
            onClick={a.confirm ? undefined : () => run(a)}
          >
            {a.label}
          </Button>
        );
        if (!a.confirm) return btn;
        return (
          <Popconfirm
            key={a.key}
            title={a.confirm}
            okText={a.label}
            cancelText="Cancel"
            disabled={!sel.count || !!busy}
            onConfirm={() => run(a)}
          >
            {btn}
          </Popconfirm>
        );
      })}
    </div>
  );
}

// One row's checkbox, positioned to sit left of a card's content.
export function RowCheck({ sel, id, style }) {
  return (
    <Checkbox
      checked={sel.isSelected(id)}
      onChange={() => sel.toggle(id)}
      onClick={(e) => e.stopPropagation()}
      style={{ marginRight: 10, ...style }}
    />
  );
}

// Run a per-row action over a batch, sequentially: these endpoints each
// write, and firing forty at once at a 2-core box is how you get a queue
// that half-applies. Reports once, with the failures named.
export async function runBatch(ids, fn, { verb = "processed" } = {}) {
  const failed = [];
  for (const id of ids) {
    try {
      await fn(id);
    } catch (e) {
      failed.push({ id, err: e?.err || e?.message || "failed" });
    }
  }
  const ok = ids.length - failed.length;
  if (ok) message.success(`${ok} ${verb}`);
  if (failed.length) {
    message.error(`${failed.length} could not be ${verb}: ${failed.slice(0, 3).map((f) => f.err).join("; ")}`);
  }
  return { ok, failed };
}

